import { Box, Checkbox, Flex, HStack, Spacer, Text, VStack } from '@chakra-ui/react';
import { addDays, format, parseISO } from 'date-fns';
import { AiOutlineFileText } from 'react-icons/ai';
import { MdOutlineWarning } from 'react-icons/md';

import { Maybe } from '../../gql/generated/graphql';

import CreditDecisionCommission from './CreditDecisionCommission';
import CreditDecisionInfoRow from './CreditDecisionInfoRow';
import CreditDecisionStatusText from './CreditDecisionStatusText';
import useCreditDecisions, { CreditDecisionWithSubmittedAt } from './useCreditDecisions';

import { formatMoney } from '../../libs/utils';
import { isCreditDecisionDenied, isCreditDecisionSelectable } from '../../utils/creditDecisions';

type CreditDecisionProps = {
  bankName: ReturnType<ReturnType<typeof useCreditDecisions>['getBankNamesByProviderLenderId']>;
  creditDecision: CreditDecisionWithSubmittedAt;
  isSelected: boolean;
  isEditable: boolean;
  setSelectedCreditDecisionId: (id?: Maybe<number>) => void;
  handleViewDetailsClick: (id?: Maybe<string>) => void;
};

const CreditDecision = ({
  bankName,
  creditDecision,
  isSelected,
  isEditable,
  setSelectedCreditDecisionId,
  handleViewDetailsClick,
}: CreditDecisionProps) => {
  const isSelectable = isCreditDecisionSelectable(creditDecision);
  const isDenied = isCreditDecisionDenied(creditDecision.application_status);

  const openDetails = () => {
    if (!creditDecision?.id) {
      return;
    }

    handleViewDetailsClick(String(creditDecision.id));
  };
  const documentationRequirements =
    creditDecision?.lender_structured_requirement?.documentation_requirements;

  const hasDocumentationRequirements = Boolean(
    documentationRequirements?.bankruptcy_discharge_required ||
      documentationRequirements?.employment_verification_required ||
      documentationRequirements?.poi_required ||
      documentationRequirements?.por_required,
  );

  return (
    <VStack
      rounded="md"
      shadow="md"
      borderWidth={1}
      borderStyle="solid"
      borderColor="queenBlue"
      bg={isSelected ? 'gray.200' : isDenied ? 'backgroundGray' : 'white'}
      justifyContent="space-between"
    >
      <VStack w="full" alignItems="flex-start" p={2} spacing={2}>
        <VStack w="full" spacing={0}>
          <HStack w="full" justifyContent="space-between">
            <Text fontSize="lg" fontWeight="normal" color={isDenied ? 'gray.500' : 'queenBlue'}>
              {bankName.displayName}
            </Text>
            <CreditDecisionCommission
              creditDecisionId={creditDecision?.id}
              creditDecisionRevision={creditDecision?.updated_at}
              isDenied={isDenied}
              lenderFees={creditDecision?.lender_fees}
            />
          </HStack>
          <HStack w="full">
            <Text fontSize="xs" color="gray.600" {...(isDenied && { color: 'gray.500' })}>
              Bank App #: {creditDecision?.provider_decision_id}
            </Text>
            <AiOutlineFileText
              size={20}
              cursor="pointer"
              color="red.500"
              style={{ marginRight: 8 }}
              onClick={openDetails}
            />
          </HStack>
        </VStack>
        <HStack>
          <CreditDecisionStatusText creditDecision={creditDecision} handleClick={openDetails} />
          {hasDocumentationRequirements && (
            <Box
              display="flex"
              alignItems="center"
              gap={1.5}
              px={2}
              py={1}
              borderWidth={1}
              borderStyle="solid"
              borderColor="americanOrange"
              borderRadius="md"
              bg="white"
              onClick={openDetails}
              cursor="pointer"
            >
              <MdOutlineWarning size={16} color="#FF8900" />
              <Text
                fontSize="xs"
                fontWeight="bold"
                color="americanOrange"
                textTransform="uppercase"
              >
                STIPS
              </Text>
            </Box>
          )}
        </HStack>
        <Flex gap={4} w="full">
          <VStack alignItems="flex-start">
            <CreditDecisionInfoRow
              isDisabled={isDenied}
              label="Buy Rate"
              value={
                creditDecision?.annual_percentage_rate
                  ? `${creditDecision.annual_percentage_rate}%`
                  : 'N/A'
              }
            />
            <CreditDecisionInfoRow
              isDisabled={isDenied}
              label="Amount"
              value={
                creditDecision?.balance_amount
                  ? `${formatMoney(creditDecision.balance_amount, { noDecimal: true })}`
                  : 'N/A'
              }
            />
            <CreditDecisionInfoRow
              isDisabled={isDenied}
              label="Submitted"
              value={format(parseISO(creditDecision.submittedAt), 'MM/dd/yyyy')}
            />
          </VStack>
          <VStack alignItems="flex-start">
            {!creditDecision.lender_fees && <Spacer h="auto" />}
            <CreditDecisionInfoRow
              isDisabled={isDenied}
              label="Term"
              value={creditDecision?.term ? `${creditDecision.term} mos` : 'N/A'}
            />
            {creditDecision.lender_fees && (
              <CreditDecisionInfoRow
                isDisabled={isDenied}
                label="Lender Fees"
                value={`$${creditDecision.lender_fees}`}
              />
            )}
            <CreditDecisionInfoRow
              isDisabled={isDenied}
              label="Expires"
              value={format(addDays(parseISO(creditDecision.submittedAt), 30), 'MM/dd/yyyy')}
            />
          </VStack>
        </Flex>
      </VStack>
      <Checkbox
        bg="queenBlue"
        roundedBottom="sm"
        color="white"
        w="full"
        p={2}
        isDisabled={!isEditable || !isSelectable || isDenied}
        isChecked={isSelected}
        onChange={(e) => setSelectedCreditDecisionId(e.target.checked ? creditDecision?.id : null)}
      >
        {!isSelectable || isDenied ? 'UNAVAILABLE' : 'SELECT'}
      </Checkbox>
    </VStack>
  );
};

export default CreditDecision;
